/**
 * ESG SaaS Platform - AI Insights API Service
 *
 * API service for AI-powered emission reduction recommendations,
 * data quality alerts, and facility benchmarking.
 */

import { api } from './api';

// ============ Types ============

export type PriorityLevel = 'high' | 'medium' | 'low';
export type ImpactLevel = 'high' | 'medium' | 'low';
export type RecommendationCategory = 'emissions' | 'benchmarking' | 'data_quality';
export type AlertSeverity = 'critical' | 'warning' | 'info';
export type AlertType = 'missing_data' | 'low_quality' | 'outlier' | 'validation_gap';

export interface Recommendation {
  id: string;
  category: RecommendationCategory;
  title: string;
  description: string;
  priority: PriorityLevel;
  impact: ImpactLevel;
  estimated_reduction_pct?: number;
  estimated_reduction_tco2e?: number;
  action_items: string[];
  affected_facility_id?: string;
  affected_facility_name?: string;
  affected_scope?: number;
  generated_at: string;
}

export interface RecommendationResponse {
  recommendations: Recommendation[];
  total_count: number;
  generated_at: string;
  cached: boolean;
  cache_expires_at?: string;
}

export interface DataQualityAlert {
  id: string;
  severity: AlertSeverity;
  alert_type: AlertType;
  title: string;
  description: string;
  affected_entity: string;
  affected_entity_type: string;
  affected_entity_id?: string;
  recommendation: string;
  data_field?: string;
  generated_at: string;
}

export interface DataQualityResponse {
  alerts: DataQualityAlert[];
  total_count: number;
  critical_count: number;
  warning_count: number;
  info_count: number;
  overall_quality_score: number;
  generated_at: string;
}

export interface FacilityBenchmark {
  facility_id: string;
  facility_name: string;
  total_emissions: number;
  scope1_emissions: number;
  scope2_emissions: number;
  scope3_emissions: number;
  floor_area_sqm?: number;
  employee_count?: number;
  intensity_per_sqm?: number;
  intensity_per_employee?: number;
  rank_in_org: number;
  percentile: number;
  performance_vs_avg: number;
  trend_vs_last_period?: number;
}

export interface BenchmarkInsight {
  id: string;
  title: string;
  description: string;
  best_performer?: string;
  worst_performer?: string;
  recommendations: string[];
}

export interface BenchmarkResponse {
  facilities: FacilityBenchmark[];
  insights: BenchmarkInsight[];
  org_average_intensity_sqm?: number;
  org_average_intensity_employee?: number;
  total_org_emissions: number;
  generated_at: string;
}

export interface InsightStatus {
  api_available: boolean;
  cache_status: string;
  cache_expires_at?: string;
  requests_remaining: number;
  rate_limit_reset_at?: string;
  last_refresh?: string;
  mock_mode: boolean;
}

export interface InsightsSummary {
  total_recommendations: number;
  emission_recommendations: number;
  data_quality_alerts: number;
  high_priority_count: number;
  facility_benchmarks: number;
  benchmark_insights: number;
  generated_at: string;
}

// ============ API Functions ============

/**
 * Get all recommendations (emissions + data quality combined)
 */
export async function getRecommendations(
  category?: RecommendationCategory
): Promise<RecommendationResponse> {
  const params = new URLSearchParams();
  if (category) {
    params.append('category', category);
  }
  const queryString = params.toString();
  const url = `/insights/recommendations${queryString ? `?${queryString}` : ''}`;
  return api.get<RecommendationResponse>(url);
}

/**
 * Get emission reduction recommendations only
 */
export async function getEmissionInsights(): Promise<RecommendationResponse> {
  return api.get<RecommendationResponse>('/insights/emissions');
}

/**
 * Get facility benchmarks with AI insights
 */
export async function getBenchmarks(): Promise<BenchmarkResponse> {
  return api.get<BenchmarkResponse>('/insights/benchmarks');
}

/**
 * Get data quality alerts
 */
export async function getDataQualityAlerts(): Promise<DataQualityResponse> {
  return api.get<DataQualityResponse>('/insights/data-quality');
}

/**
 * Force refresh of cached insights (admin only)
 */
export async function refreshInsights(): Promise<RecommendationResponse> {
  return api.post<RecommendationResponse>('/insights/refresh', {});
}

/**
 * Get AI insights API status
 */
export async function getInsightStatus(): Promise<InsightStatus> {
  return api.get<InsightStatus>('/insights/status');
}

/**
 * Get insights summary for dashboard widgets
 */
export async function getInsightsSummary(): Promise<InsightsSummary> {
  return api.get<InsightsSummary>('/insights/summary');
}

// ============ Utility Functions ============

/**
 * Get color for priority level
 */
export function getPriorityColor(priority: PriorityLevel): 'error' | 'warning' | 'success' {
  switch (priority) {
    case 'high':
      return 'error';
    case 'medium':
      return 'warning';
    case 'low':
      return 'success';
    default:
      return 'warning';
  }
}

/**
 * Get color for alert severity
 */
export function getSeverityColor(severity: AlertSeverity): 'error' | 'warning' | 'info' {
  switch (severity) {
    case 'critical':
      return 'error';
    case 'warning':
      return 'warning';
    case 'info':
      return 'info';
    default:
      return 'info';
  }
}

/**
 * Get icon name for recommendation category
 */
export function getCategoryIcon(category: RecommendationCategory): string {
  switch (category) {
    case 'emissions':
      return 'eco';
    case 'benchmarking':
      return 'bar_chart';
    case 'data_quality':
      return 'fact_check';
    default:
      return 'lightbulb';
  }
}

/**
 * Format percentage reduction
 */
export function formatReduction(pct?: number): string {
  if (pct === undefined || pct === null) {
    return 'TBD';
  }
  return `-${pct.toFixed(0)}%`;
}
